import { useEffect, useState } from "react";
import axios from "axios";
import instance from "lib/axiosInterceptor";
import { baseUrl } from "utils/baseUrl";
import { SpeakerSettingsResponse } from "interfaces/interfaces-data";
import {
  Speaker,
  VideoHighlightProps,
} from "interfaces/ExternalPage/SpeakerLandingPage.interface";
import stagePresenter from "assets/image/stage-presenter.jpg";

interface UseSpeakerDataReturn {
  speakerData: Speaker;
  setSpeakerData: React.Dispatch<React.SetStateAction<Speaker>>;
  expertise: string[];
  videoHighlight: VideoHighlightProps;
  isSpeakerLoading: boolean;
}

export function useSpeakerData(username?: string): UseSpeakerDataReturn {
  const [speakerData, setSpeakerData] = useState<Speaker>({
    id: "",
    name: "",
    location: "",
    phone: null,
    email: null,
    description: "",
    rating: 0,
    verifiedRating: 0,
    availability: false,
    images: [],
    videos: [],
    expertise: [],
    socials: [],
    company: "",
    designation: "",
    events: 0,
    badges: [],
    speakerId: "",
    attendees: 0,
    currency: null,
    minimumAmount: 0,
    maximumAmount: 0,
    availableCountries: null,
    engagementScore: 0,
    canBeInvited: false,
  });

  const [expertise, setExpertise] = useState<string[]>([]);

  const [videoHighlight, setVideoHighlight] = useState<VideoHighlightProps>({
    thumbnail: stagePresenter,
  });

  const [isSpeakerLoading, setIsSpeakerLoading] = useState(true);

  useEffect(() => {
    const getSpeakerData = async () => {
      if (!username) {
        setIsSpeakerLoading(false);
        return;
      }
      setIsSpeakerLoading(true);
      try {
        const result = await axios.get<SpeakerSettingsResponse>(
          `${baseUrl}/speaker/${username}`,
        );

        const data = result.data;

        // Helper to filter out "NA" values
        const isValidValue = (value: string | undefined | null): boolean => {
          return !!value && value !== "NA";
        };

        // Update speaker data only if values exist and are not "NA"
        if (data.name || data.designation || data.location || data.company) {
          setSpeakerData((prev) => ({
            ...prev,
            ...(isValidValue(data.name) && { name: data.name }),
            ...(isValidValue(data.location) && { location: data.location }),
            ...(data.phone && { phone: data.phone }),
            ...(data.email && { email: data.email }),
            ...(isValidValue(data.description) && {
              description: data.description,
            }),
            ...(data.rating && { rating: data.rating }),
            ...(data.verifiedRating && { verifiedRating: data.verifiedRating }),
            ...(data.availability && { availability: data.availability }),
            ...(data.images &&
              data.images.length > 0 && { images: data.images }),
            ...(isValidValue(data.designation) && {
              designation: data.designation,
            }),
            ...(data.events && { events: data.events }),
            ...(isValidValue(data.company) && { company: data.company }),
            ...(data.badges && { badges: data.badges }),
            ...(data.id && { speakerId: data.id }),
            ...(data.socials && { socials: data.socials }),
            ...(data.attendees && { attendees: data.attendees }),
            ...(isValidValue(data.currency) && { currency: data.currency }),
            ...(data.minimumAmount !== undefined &&
              data.minimumAmount !== null && {
                minimumAmount: data.minimumAmount,
              }),
            ...(data.maximumAmount !== undefined &&
              data.maximumAmount !== null && {
                maximumAmount: data.maximumAmount,
              }),
            ...(data.availableCountries &&
              data.availableCountries.length > 0 && {
                availableCountries: data.availableCountries,
              }),
            ...(data.engagementScore && {
              engagementScore: data.engagementScore,
            }),
            ...(data.canBeInvited !== undefined && {
              canBeInvited: data.canBeInvited,
            }),
          }));
        }

        // Update expertise if exists
        if (data.expertise && data.expertise.length > 0) {
          setExpertise(data.expertise);
        }

        // Update video if exists
        if (data.videos && data.videos.length > 0 && data.videos[0]) {
          setVideoHighlight((prev) => ({
            ...prev,
            videoUrl: data.videos[0],
          }));
        }
      } catch (e: any) {
        console.error("Error fetching speaker data:", e);
      } finally {
        setIsSpeakerLoading(false);
      }
    };

    getSpeakerData();
  }, [username]);

  return {
    speakerData,
    setSpeakerData,
    expertise,
    videoHighlight,
    isSpeakerLoading,
  };
}

export default useSpeakerData;
